import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatBRL, formatBRLc } from "@/lib/api";
import { CarCard } from "@/components/CarCard";
import { AssistantChat } from "@/components/AssistantChat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QrCode, Gift, Printer, Search, ArrowRight, ShieldCheck, BadgeCheck, TrendingDown } from "lucide-react";

export default function Home() {
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [sold, setSold] = useState([]);
  const [belowFipe, setBelowFipe] = useState([]);
  const [belowCount, setBelowCount] = useState(0);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/listings").then(({ data }) => setCars(data.slice(0, 8))).catch(() => {});
    api.get("/listings-sold-recent").then(({ data }) => setSold(data)).catch(() => {});
    api.get("/listings-below-fipe", { params: { limit: 4 } }).then(({ data }) => setBelowFipe(data)).catch(() => {});
    api.get("/listings-below-fipe/count").then(({ data }) => setBelowCount(data.count || 0)).catch(() => {});
  }, []);

  const search = (e) => {
    e.preventDefault();
    navigate(`/buscar?q=${encodeURIComponent(q)}`);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-obsidian text-white">
        <img src="https://images.pexels.com/photos/15560435/pexels-photo-15560435.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="carro" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-green-600 text-white rounded-full pl-2 pr-4 py-1.5 mb-6 shadow-lg" data-testid="hero-free-badge">
              <span className="bg-white/20 rounded-full p-1"><Gift className="w-4 h-4" /></span>
              <span className="font-800 text-sm uppercase tracking-wide">Anúncio 100% grátis, sempre</span>
            </motion.div>
            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="overline text-blaze mb-5">A nova forma de vender seu carro</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="font-heading font-900 text-5xl md:text-6xl leading-[0.95]">
              Anuncie seu carro <span className="text-blaze">de graça.</span><br />Divulgue com QR Code.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              className="mt-6 text-lg text-white/70 max-w-xl">
              No PicCar o anúncio é 100% gratuito e aparece pra todo mundo na busca. Se quiser divulgar na rua, você assina o <b className="text-white">Kit de Divulgação com QR</b> e recebe cartaz, cartão postal e cartão de visita — quem escaneia cai direto no seu anúncio.
            </motion.p>

            <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
              onSubmit={search} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="hero-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busque por marca ou modelo..."
                  className="h-14 pl-12 bg-white text-obsidian border-0 rounded-sm text-base" data-testid="hero-search-input" />
              </div>
              <Button type="submit" className="h-14 px-8 bg-blaze hover:bg-white hover:text-obsidian text-white font-700 rounded-sm text-base" data-testid="hero-search-btn">
                Buscar
              </Button>
            </motion.form>
            <div className="mt-4 flex items-center gap-2 text-sm text-white/50">
              <Gift className="w-4 h-4 text-blaze" /> Anúncio sempre gratuito, sem limite de fotos
            </div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
            className="lg:col-span-4 hidden lg:block">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-sm p-8">
              <QrCode className="w-14 h-14 text-blaze" strokeWidth={1.5} />
              <p className="mt-5 font-heading font-800 text-2xl">Do vidro do carro pro celular</p>
              <p className="mt-2 text-sm text-white/60">Quem vê seu carro na rua aponta a câmera no QR e abre seu anúncio na hora — com fotos, preço e chat seguro. Sem digitar nada.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ASSISTENTE DE IA */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 -mt-8 relative z-10" data-testid="home-assistant">
        <AssistantChat />
      </section>

      {/* CHAMARIZ: contador de ofertas abaixo da FIPE */}
      {belowCount > 0 && (
        <Link to="/buscar?sort=fipe_desc" data-testid="below-fipe-counter"
          className="block bg-emerald-500 hover:bg-emerald-600 transition-colors text-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-center gap-2 text-sm sm:text-base font-700">
            <TrendingDown className="w-5 h-5" strokeWidth={2} />
            <span><b className="font-900">{belowCount}</b> {belowCount === 1 ? "carro está" : "carros estão"} abaixo da tabela FIPE agora</span>
            <span className="hidden sm:inline-flex items-center gap-1 underline underline-offset-2">Ver ofertas <ArrowRight className="w-4 h-4" /></span>
          </div>
        </Link>
      )}

      {/* DIFERENCIAIS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Gift, t: "Anúncio grátis", d: "Cadastre seu carro sem pagar nada. Fotos, preço, KM e contato — tudo liberado e visível pra todos na busca." },
            { icon: Printer, t: "Kit de Divulgação com QR", d: "Assine (R$ 29 a cada 3 meses) e receba 3 materiais prontos — cartaz A4, cartão postal e cartão de visita — todos com o QR exclusivo que leva ao seu anúncio." },
            { icon: ShieldCheck, t: "Contato protegido", d: "Seu telefone fica oculto. Interessados falam com você pelo chat seguro do PicCar." },
          ].map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }} className="bg-white border border-border rounded-sm p-8" data-testid={`diff-card-${i}`}>
              <div className="w-12 h-12 rounded-sm bg-obsidian flex items-center justify-center">
                <f.icon className="w-6 h-6 text-blaze" strokeWidth={1.5} />
              </div>
              <p className="mt-5 font-heading font-800 text-xl">{f.t}</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline text-blaze mb-2">Recém anunciados</p>
            <h2 className="font-heading font-800 text-3xl md:text-4xl">Carros na vitrine</h2>
          </div>
          <Link to="/buscar" className="hidden sm:flex items-center gap-1 text-sm font-700 hover:text-blaze transition-colors" data-testid="ver-todos-link">
            Ver todos <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {cars.length === 0 ? (
          <div className="bg-white border border-border rounded-sm p-16 text-center">
            <p className="text-muted-foreground">Ainda não há carros anunciados. Seja o primeiro!</p>
            <Button className="mt-4 bg-obsidian hover:bg-blaze text-white rounded-sm" onClick={() => navigate("/anunciar")} data-testid="empty-anunciar-btn">Anunciar meu carro</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {cars.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
          </div>
        )}
      </section>

      {/* OFERTAS ABAIXO DA FIPE */}
      {belowFipe.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="below-fipe-section">
          <div className="flex items-end justify-between mb-8">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-sm bg-emerald-500 flex items-center justify-center shrink-0">
                <TrendingDown className="w-6 h-6 text-white" strokeWidth={2} />
              </span>
              <div>
                <p className="overline text-emerald-600 mb-1">Oportunidades</p>
                <h2 className="font-heading font-800 text-2xl md:text-3xl">Ofertas abaixo da tabela FIPE</h2>
              </div>
            </div>
            <Link to="/buscar?sort=fipe_desc" className="hidden sm:flex items-center gap-1 text-sm font-700 hover:text-blaze transition-colors" data-testid="ver-ofertas-link">
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {belowFipe.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
          </div>
        </section>
      )}

      {/* VENDIDOS RECENTEMENTE (prova social) */}
      {sold.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="vendidos-section">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-11 h-11 rounded-sm bg-blaze flex items-center justify-center shrink-0">
              <BadgeCheck className="w-6 h-6 text-white" strokeWidth={2} />
            </span>
            <div>
              <p className="overline text-blaze mb-1">Prova social</p>
              <h2 className="font-heading font-800 text-2xl md:text-3xl">Vendidos recentemente no PicCar</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {sold.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="bg-obsidian rounded-sm p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading font-900 text-3xl md:text-4xl text-white">Pronto para vender?</h2>
            <p className="mt-2 text-white/60">Anuncie grátis agora. Assine o Kit de Divulgação com QR quando quiser por {formatBRLc(29)} a cada 3 meses.</p>
          </div>
          <Button className="h-14 px-10 bg-blaze hover:bg-white hover:text-obsidian text-white font-700 rounded-sm text-base" onClick={() => navigate("/anunciar")} data-testid="cta-anunciar-btn">
            Anunciar Grátis <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
